import {
  Box,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';

import { ExpandMoreRounded } from '@mui/icons-material';

import LogoIcon from '@/shared/assets/icons/logo.svg?react';

import { useActions } from '../../../../hooks/useActions';
import { classNames } from '../../../../utils';
import {
  getProductIdsConsts,
  mapIconsNameToIcon,
} from '../../../../utils/wizard-helper';

import { ButtonUi } from '../../Button/Button';
import { productDropdownStyles } from './style';

const popperModifier = [
  {
    name: 'offset',
    options: {
      offset: [0, 8],
    },
  },
];

export const ProductDropdown = memo((props) => {
  const { selectedId, handleChange, className, disabled } = props;
  const { t } = useTranslation();

  const { productDropdown } = productDropdownStyles;

  const { getAllProductConfigurations, getAllProductConfigurationDefinition } =
    useActions();

  const { products, isLoading: isProductsLoading } = useSelector(
    (state) => state.products,
  );

  const { practitioner } = useSelector((state) => state.authentication);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const { MHId } = getProductIdsConsts(products);

  /**
   * Set's dropdown Icon
   */
  const dropdownIcon = useMemo(() => {
    if (!selectedProduct?.text) return <LogoIcon />;
    return mapIconsNameToIcon(selectedProduct?.text);
  }, [selectedProduct]);

  /**
   * Generates products options
   */
  const options = useMemo(
    () =>
      [...new Set(products)]
        // TODO remove filtering Hypnosis product
        .filter((product) => {
          // Medical Hypnosis and Future Me products IDs
          const filteredProductsId = [MHId];
          return !filteredProductsId.includes(product?.id);
        })
        .map(({ name, id }) => ({
          value: id,
          text: name,
        })),
    [products, MHId],
  );

  /**
   * Toggle product dropdown state
   * @param event native event
   */
  const toggleDropdown = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    setDropdownOpen((prev) => !prev);
  }, []);

  /**
   * Close product dropdown
   */
  const handleClickAway = () => setDropdownOpen(false);

  /**
   * Select product
   * @param product selected product
   */
  const selectProduct = useCallback(
    (product) => {
      setDropdownOpen(false);
      setSelectedProduct(product);
      handleChange(product);
    },
    [handleChange],
  );

  /**
   * Dropdown optional class names
   */
  const dropdownMods = {
    open: dropdownOpen,
  };

  /**
   * Sets autocomplete value to selectedId
   */
  useEffect(() => {
    if (selectedId)
      setSelectedProduct(options.find((item) => item.value === selectedId));
    else setSelectedProduct(null);
  }, [options, selectedId]);

  /**
   * Fetches all teams if they are not defined
   */
  useEffect(() => {
    // Get all the devices for the practitioner user
    if (products.length <= 0 && practitioner) getAllProductConfigurations();
    if (products.length <= 0 && !practitioner)
      getAllProductConfigurationDefinition();
  }, []);

  return (
    <Box
      sx={productDropdown}
      className={classNames('product-dropdown', dropdownMods, [className])}
    >
      <ButtonUi
        className={classNames('product-btn', {}, [className])}
        variant='contained'
        type='primary'
        size='small'
        onClickHandler={toggleDropdown}
        startIcon={dropdownIcon}
        endIcon={<ExpandMoreRounded />}
        disabled={disabled}
      >
        {selectedProduct?.text || t('products.all-products')}
      </ButtonUi>
      <Popper
        className='dropdown-popper'
        open={dropdownOpen}
        anchorEl={anchorEl}
        transition
        disablePortal
        modifiers={popperModifier}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'center top' }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClickAway}>
                <MenuList id='split-button-menu' autoFocusItem>
                  <MenuItem
                    key='all-products'
                    selected={!selectedProduct}
                    onClick={() => selectProduct(null)}
                  >
                    {t('products.all-products')}
                  </MenuItem>
                  {options.map((option) => (
                    <MenuItem
                      key={option?.value}
                      selected={option?.value === selectedProduct?.value}
                      onClick={() => selectProduct(option)}
                    >
                      {option?.text}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
});
